import { AccountType } from 'src/utils/enums'

import { useAccountProfile } from './useAccountProfile'

export type CreationRights = {
  canCreateUrbanAnalysis: boolean
  canCreateProject: boolean
  isLoading: boolean
}

/**
 * What the signed-in commercial's company is allowed to create (#655).
 *
 * The two rights live on `companies` and are read THROUGH the account's
 * relation to it — `salesmens.company` for a SALESMAN, `accounts.company` for a
 * SOCIETY. They are never copied onto the salesman, so a commercial added to an
 * already-restricted company is bound the moment they exist.
 *
 * Resolved through `useAccountProfile`, so it shares the `account-header-info`
 * React Query entry the sider and the hub greeting already populate: gating a
 * button costs no extra request. Same reason `useOwnSalesmanId` is built this
 * way.
 *
 * Two deliberate answers callers depend on:
 *
 * - **Fail closed while loading, and on a missing company.** Callers render
 *   nothing rather than a CTA they must retract a frame later, and a commercial
 *   whose company cannot be resolved is treated as "cannot create" — never as
 *   "let the server decide". `useOwnSalesmanId` sets the same precedent.
 * - **Every non-commercial role gets `true`.** The restriction binds commercial
 *   self-service only, so an architect is never gated here. That keeps
 *   `isProjectHubRole` the thing that decides WHETHER the hub renders, and this
 *   hook the thing that decides WHAT it offers — no role branch at the call site.
 *
 * This is the gate, not the guard: enforcement lives server-side in
 * `assertCompanyMayCreate`. Hiding a button is a courtesy to the commercial.
 */
export const useCreationRights = (): CreationRights => {
  const { profile, accountType, isLoading } = useAccountProfile()

  const isCommercial =
    accountType === AccountType.SALESMAN || accountType === AccountType.SOCIETY

  if (!isCommercial) {
    return {
      canCreateUrbanAnalysis: true,
      canCreateProject: true,
      isLoading: false,
    }
  }

  if (isLoading) {
    return {
      canCreateUrbanAnalysis: false,
      canCreateProject: false,
      isLoading: true,
    }
  }

  // Narrowed on the literals rather than the `AccountType` constants: the
  // shared enum is not typed as the discriminant of `ProfileData`, so only the
  // literal collapses the union. Same reason `useOwnSalesmanId` does it.
  const company =
    profile?.account_type === 'SALESMAN'
      ? profile.salesmen?.company
      : profile?.account_type === 'SOCIETY'
        ? profile.company
        : null

  return {
    canCreateUrbanAnalysis: company?.can_create_urban_analysis ?? false,
    canCreateProject: company?.can_create_projects ?? false,
    isLoading: false,
  }
}
